import moment from "moment";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import submit from "submit-form";
import ConfirmationModal from "../../../../parts/modals/ConfirmationModal";
import SvgIcon from "../../../../parts/SvgIcon";
import ToolTip from "../../../../parts/tooltip";

const publishWorkflow = (id, options, t) => {
  const publishedOption = Object.keys(options).some((key) => {
    return options[key].status === "published";
  });
  if (!publishedOption) {
    return alert(t("customWorkflows.publishOneOptionFirst"));
  }
  submit(`/actions/custom-workflows/publish/${id}`, {
    method: "GET",
  });
};

const unpublishWorkflow = (id) => {
  submit(`/actions/custom-workflows/unpublish/${id}`, {
    method: "GET",
  });
};

const WorkflowToggle = ({ status, id, options }) => {
  const { t } = useTranslation("coaching-portal");
  const published = status === "published";
  const [openedConfirmationModal, setOpenedConfirmationModal] = useState(false);

  const handleChange = () => {
    published ? unpublishWorkflow(id) : publishWorkflow(id, options, t);
    setTimeout(() => {
      setOpenedConfirmationModal(false);
    }, 250);
  };
  return (
    <>
      <input
        type="checkbox"
        id={`switch-${id}`}
        name={`switch-${id}`}
        role="switch"
        onChange={() => {
          setOpenedConfirmationModal((prev) => !prev);
        }}
        checked={published}
      />
      <ConfirmationModal
        title={
          published
            ? t("customWorkflows.unpublish")
            : t("customWorkflows.publish")
        }
        contentText={
          published
            ? t("customWorkflows.confirmUnpublishWorkflow")
            : t("customWorkflows.confirmPublishWorkflow")
        }
        cancelButtonText={t("customWorkflows.cancel")}
        cancelButtonAction={() => {
          setOpenedConfirmationModal((prev) => !prev);
        }}
        confirmButtonText={
          published
            ? t("customWorkflows.unpublish")
            : t("customWorkflows.publish")
        }
        confirmButtonAction={() => handleChange()}
        isOpen={openedConfirmationModal}
        confirmSuccess={!published}
      />
    </>
  );
};

export const CustomWorkflowsList = ({ workflows }) => {
  const { t } = useTranslation("coaching-portal");
  return (
    <section>
      {workflows?.map(({ _id, status, type, options, access, publishedAt }) => {
        // This is just to show the option on the Getting Started card, doesn't affect anything else
        // If you use the options directly it will affect the workflow
        // We don't want meme ids on the actual db
        const modifiedOptions = { ...options };
        if (
          access === "pull" &&
          type === "getting-started" &&
          !modifiedOptions.questionnaire
        ) {
          modifiedOptions.questionnaire = {
            _id: "just-click-edit-workflow-bro",
            status: "draft",
          };
        }

        return (
          <article key={_id} id={_id} className="workflow-card">
            <div className="body">
              <p className="s2">
                {t(`customWorkflows.workflowName.${type}`) || ""}
              </p>
              <p className="p5 mt-1 mb-4">
                {t("customWorkflows.optionsCount", {
                  count: Object.keys(modifiedOptions).length,
                })}
              </p>
              <div className={`status-chip ${status}`}>
                <span className="capitalize">
                  {status === "published"
                    ? t("customWorkflows.statusPublished")
                    : t("customWorkflows.statusDraft")}
                </span>
                <ToolTip
                  id="status-tooltip"
                  body={
                    status === "published"
                      ? t("customWorkflows.statusTooltipPublished")
                      : t("customWorkflows.statusTooltipDraft")
                  }
                >
                  <span className="status-chip-icon">?</span>
                </ToolTip>
              </div>
              {access === "pull" && status === "published" && publishedAt && (
                <p className="p5 italic mb-2 -mt-2 pb-2 border-b border-transparent border-b-(--white-10) border-solid">
                  {t("customWorkflows.pullPublishedInviteNotice", {
                    date: moment(publishedAt).format("LL"),
                  })}
                </p>
              )}
              {Object.keys(modifiedOptions).map((key) => (
                <div className="option" key={modifiedOptions[key]._id ?? key}>
                  <SvgIcon
                    src={
                      modifiedOptions[key].status === "published"
                        ? "/img/new-icons/tick-square.svg"
                        : "/img/new-icons/info-circle.svg"
                    }
                    noFilterOnLight
                  />
                  <p className="p5">{t(`customWorkflows.optionName.${key}`)}</p>
                </div>
              ))}
            </div>
            <footer>
              <button
                data-button-type="text"
                className="s2 clipped"
                onClick={() => {
                  submit(`/actions/refresh/?`, {
                    method: "GET",
                    body: {
                      workflow: _id,
                      type,
                    },
                  });
                }}
              >
                <span>{t("customWorkflows.editWorkflow")}</span>
              </button>
              <WorkflowToggle status={status} id={_id} options={options} />
            </footer>
          </article>
        );
      })}
    </section>
  );
};
